import React, { useId, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { BsChevronRight, BsChevronDown } from 'react-icons/bs';
import { TbMessageCircle2 } from 'react-icons/tb';
import { FcCheckmark } from 'react-icons/fc';
import { BiEdit } from 'react-icons/bi';
import { format } from 'date-fns';
import { useChatContext } from '@company/state/chatContext';
import { useJobAssignmentStatus } from '@hooks/useJobAssignmentStatus';
import { completeOnboarding } from '@services/activity-tracker/activityTrackerService';
import { getFormattedShiftTimes } from '@services/shift/shiftService';
import IJobDetail, {
  JobAssignment,
  ShiftPosition,
} from '@th-types/job.detail.type';
import { ActivityDetail } from '@th-types/daily-activity.type';
import { JobShiftsQuickFilters, ShiftStatus } from '@th-types/jobs.type';
import {
  ActivityTrackerStatus,
  getCheckInValue,
  getCheckOutValue,
  getJobShiftActivityStatus,
  SMALL_ICONS_SIZE,
} from '@utils/JobUtils';
import DATE_FORMATS from '@constants/dateFormat';
import { isShiftPayable } from '@utils/ShiftsUtils';

import '../style.scss';

interface Props {
  selectedFilter?: JobShiftsQuickFilters;
  job: IJobDetail;
  activities: ActivityDetail[];
  openChatAction?: () => void;
  completeOnboardingAction: (
    jobAssignment: JobAssignment,
    complete: boolean
  ) => void;
  handleConfirmMarkPaid: (activity: ActivityDetail) => void;
  handleMissingHourNoticed: (shiftPositionId: number) => void;
  handleMissingActivityNoticed: (shiftPositionId: number) => void;
  handleOpenEditShiftModal: (shiftPosition: ShiftPosition) => void;
}

function JobShiftsTableItemMobile({
  job,
  activities,
  selectedFilter,
  openChatAction,
  handleConfirmMarkPaid,
  handleOpenEditShiftModal,
  completeOnboardingAction,
  handleMissingHourNoticed,
  handleMissingActivityNoticed,
}: Props) {
  const jobAssignmentStatus = useJobAssignmentStatus()(undefined);
  const { shouldShowWorkerInfoInChat } = jobAssignmentStatus;
  const { openChat } = useChatContext();

  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const handleExpandRow = (shiftId: number) => {
    const isRowExpanded = expandedRows.includes(shiftId);
    const newExpandedRows = isRowExpanded
      ? expandedRows.filter((id) => id !== shiftId)
      : expandedRows.concat(shiftId);

    const obj: { [key: number]: boolean } = {};
    obj[shiftId] = isRowExpanded;
    setExpandedRows(newExpandedRows);
  };

  const divKey = useId();
  const activityKey = useId();

  const getTableHeader = () => {
    switch (selectedFilter) {
      case JobShiftsQuickFilters.CURRENT_OPEN_SHIFTS:
        return ['POSITION', 'WAGE'];
      case JobShiftsQuickFilters.NO_CHECK_IN_OUT:
        return ['WORKER', 'CHECK IN', 'CHECK OUT'];
      case JobShiftsQuickFilters.MARK_PAID:
        return ['WORKER', 'SHIFT PAID?'];
      case JobShiftsQuickFilters.ONBOARDING:
        return ['WORKER', 'SHIFT STATUS', 'ONBOARDING'];
      case JobShiftsQuickFilters.MISSING_HOURS:
      case JobShiftsQuickFilters.TO_APPROVE:
        return ['WORKER', 'SUBMITTED TIME'];
      default:
        return ['WORKER', 'SHIFT STATUS'];
    }
  };

  const headerHasThreeColumns = () => {
    return [
      JobShiftsQuickFilters.ONBOARDING,
      JobShiftsQuickFilters.NO_CHECK_IN_OUT,
    ].some((item) => item === selectedFilter);
  };

  const handleOpenChat = (activityDetail: ActivityDetail) => {
    if (activityDetail.shiftPosition.jobAssignment) {
      openChat(
        activityDetail.shiftPosition.jobAssignment.worker,
        activityDetail.shiftPosition.jobAssignment.id,
        shouldShowWorkerInfoInChat({
          status: activityDetail.shiftPosition.jobAssignment.status,
        })
      );
      if (openChatAction) {
        openChatAction();
      }
    }
  };

  const triggerMissingHourNoticed = (
    activity: ActivityDetail,
    activityStatus: ActivityTrackerStatus
  ) => {
    if (
      expandedRows.includes(activity.shiftPosition.id) &&
      activityStatus === ActivityTrackerStatus.MISSING_HOURS
    ) {
      handleMissingHourNoticed(activity.shiftPosition.id);
    }
  };

  const getShiftStatus = (activity: ActivityDetail, isHeader: boolean) => {
    const activityStatus = getJobShiftActivityStatus(job, activity);
    if (isHeader) {
      return (
        <Col
          xs={headerHasThreeColumns() ? 4 : 5}
          className={`column-field-style d-flex justify-content-center align-items-center shift-status-colors shift-status-colors__${activityStatus
            .replace(' ', '_')
            .replace('/', '_')
            .toLowerCase()}${activity.shiftPosition.noticedMissingHours ? '__noticed' : ''}`}
          onClick={() => {
            triggerMissingHourNoticed(activity, activityStatus);
          }}
        >
          <span>{activityStatus}</span>
        </Col>
      );
    }
    return (
      <Col
        xs={6}
        style={{ maxWidth: '150px' }}
        className={`column-field-style ms-2 d-flex justify-content-center align-items-center text-center shift-status-colors shift-status-colors__${activityStatus
          .replace(' ', '_')
          .replace('/', '_')
          .toLowerCase()}${activity.shiftPosition.noticedMissingHours ? '__noticed' : ''}`}
        onClick={() => {
          triggerMissingHourNoticed(activity, activityStatus);
        }}
      >
        <span>{activityStatus}</span>
      </Col>
    );
  };

  const getSubmittedTimeColumn = (
    activity: ActivityDetail,
    isHeader: boolean
  ) => {
    return (
      <Col
        className={`${isHeader ? 'text-center' : 'text-start'} column-field-style`}
      >
        {activity.timeSheet?.localizedStart &&
        activity.timeSheet?.localizedEnd ? (
          <span>
            {`${format(
              new Date(activity.timeSheet.localizedStart),
              DATE_FORMATS.TIME_FORMAT
            )} - ${format(
              new Date(activity.timeSheet.localizedEnd),
              DATE_FORMATS.TIME_FORMAT
            )}`}
          </span>
        ) : (
          <span> - </span>
        )}
      </Col>
    );
  };

  const getMarkPaidColumn = (activity: ActivityDetail, isHeader: boolean) => {
    if (activity.shiftPosition.shiftStatus !== ShiftStatus.CONFIRMED) {
      return (
        <Col
          className={`${isHeader ? 'text-center' : 'text-start column-field-style'}`}
        >
          {' '}
          -{' '}
        </Col>
      );
    }
    return (
      <Col
        className={`${isHeader ? 'text-center' : 'text-start column-field-style'}`}
      >
        {activity.shiftPosition.isPaid ? (
          <FcCheckmark
            data-testid="icon-fc-checkmark"
            onClick={() => {
              handleConfirmMarkPaid(activity);
            }}
            className="cursor-point"
          />
        ) : (
          <Button
            size="sm"
            variant="light"
            style={{ fontSize: '14px', padding: '0px 5px' }}
            disabled={!isShiftPayable(job, activity)}
            onClick={() => handleConfirmMarkPaid(activity)}
          >
            MARK PAID
          </Button>
        )}
      </Col>
    );
  };

  const getPayRateColumn = (activity: ActivityDetail, isHeader: boolean) => {
    return (
      <Col
        className={`${isHeader ? 'text-center' : 'text-start column-field-style'}`}
        xs={`${headerHasThreeColumns() ? 4 : 5}`}
      >
        ${activity.shiftPosition.wage.payRate}/
        {activity.shiftPosition.wage.payRatePeriod}
      </Col>
    );
  };

  const triggerCompleteOnboarding = (activity: ActivityDetail) => {
    if (activity.shiftPosition.jobAssignment) {
      const { jobAssignment } = activity.shiftPosition;
      completeOnboarding([jobAssignment], activities, true);
      const isOnboardingCompleted = jobAssignment.onBoardingCompleted;
      completeOnboardingAction(jobAssignment, !isOnboardingCompleted);
    }
  };

  const getOnboardingButton = (activity: ActivityDetail) => {
    if (!activity.shiftPosition.jobAssignment) {
      return <div />;
    }
    if (!activity.shiftPosition.jobAssignment.onBoardingCompleted) {
      return (
        <Button
          size="sm"
          variant="light"
          data-testid="complete-onboarding-button"
          className="column-field-style row-at-button ms-0"
          onClick={() => triggerCompleteOnboarding(activity)}
        >
          COMPLETE
        </Button>
      );
    }
    return (
      <FcCheckmark
        color="var(--black)"
        data-testid="checkmark-completed-onboarding"
        className="cursor-point mb-2"
        onClick={() => triggerCompleteOnboarding(activity)}
      />
    );
  };

  const getFirstColumn = (activity: ActivityDetail) => {
    switch (selectedFilter) {
      case JobShiftsQuickFilters.CURRENT_OPEN_SHIFTS:
        return (
          <Col xs={6} className="text-start column-field-style">
            {job.title.name}
          </Col>
        );
      default:
        return (
          <Col
            xs={`${headerHasThreeColumns() ? 4 : 6}`}
            className="text-start column-field-style"
          >
            {activity.shiftPosition.jobAssignment?.worker.fullName}
          </Col>
        );
    }
  };

  const getSecondColumn = (activity: ActivityDetail) => {
    switch (selectedFilter) {
      case JobShiftsQuickFilters.CURRENT_OPEN_SHIFTS:
        return getPayRateColumn(activity, true);
      case JobShiftsQuickFilters.NO_CHECK_IN_OUT:
        return (
          <Col
            xs={`${headerHasThreeColumns() ? 4 : 5}`}
            className="text-center d-flex justify-content-center align-items-center column-field-style"
          >
            {getCheckInValue(job, activity)}
          </Col>
        );
      case JobShiftsQuickFilters.MISSING_HOURS:
        return (
          <Col
            xs={`${headerHasThreeColumns() ? 4 : 5}`}
            className="text-center d-flex column-field-style"
          >
            {getSubmittedTimeColumn(activity, true)}
            <BiEdit size={SMALL_ICONS_SIZE} />
          </Col>
        );
      case JobShiftsQuickFilters.TO_APPROVE:
        return getSubmittedTimeColumn(activity, true);
      case JobShiftsQuickFilters.MARK_PAID:
        return getMarkPaidColumn(activity, true);
      case JobShiftsQuickFilters.ONBOARDING:
      default:
        return getShiftStatus(activity, true);
    }
  };

  const getThirdColumn = (activity: ActivityDetail) => {
    switch (selectedFilter) {
      case JobShiftsQuickFilters.NO_CHECK_IN_OUT:
        return (
          <Col
            xs={3}
            className="d-flex justify-content-center align-items-center column-field-style"
          >
            {getCheckOutValue(job, activity)}
          </Col>
        );
      case JobShiftsQuickFilters.ONBOARDING:
      default:
        return (
          <Col
            xs={3}
            className="d-flex justify-content-center align-items-center column-field-style"
          >
            {getOnboardingButton(activity)}
          </Col>
        );
    }
  };

  const renderActionButtons = (activity: ActivityDetail) => {
    const isShiftOpen = activity.shiftPosition.shiftStatus === ShiftStatus.OPEN;
    return (
      <Row className="my-2 ps-0">
        {!isShiftOpen && (
          <Col xs={6} className="text-start">
            <Button
              variant="var(--light)"
              className="ps-0"
              onClick={() => handleOpenChat(activity)}
            >
              <TbMessageCircle2
                className="mb-1"
                color="var(--black)"
                size={18}
              />
              <span className="ps-1">OPEN CHAT</span>
            </Button>
          </Col>
        )}

        <Col xs={6} className={isShiftOpen ? 'text-start' : 'text-end'}>
          <Button
            variant="var(--light)"
            className={isShiftOpen ? 'ps-0' : ''}
            onClick={() => handleOpenEditShiftModal(activity.shiftPosition)}
          >
            <BiEdit
              color="var(--black)"
              className="mb-1 cursor-point"
              size={20}
            />
            <span className="ps-1">EDIT SHIFT</span>
          </Button>
        </Col>
      </Row>
    );
  };

  const getColSize = (index: number) => {
    if (!headerHasThreeColumns()) {
      return index === 0 ? 6 : 5;
    }
    return index < 2 ? 4 : 3;
  };

  return (
    <div key={divKey} className="ms-2 mb-1 align-items-center">
      <hr className="table-horizontal-line" />
      <Row className="mt-2">
        {getTableHeader().map((header, index) => (
          <Col
            xs={getColSize(index)}
            className={`table-header ${index === 0 ? 'text-start' : 'text-center px-0'}`}
            key={`${header}`}
          >
            <strong>{header}</strong>
          </Col>
        ))}
        <Col xs={1} />
      </Row>

      {activities.map((activity) => (
        <React.Fragment key={`${activityKey}-${activity.shiftPosition.id}`}>
          <Row
            onClick={() => handleExpandRow(activity.shiftPosition.id)}
            className="cursor-point d-flex my-2"
          >
            {getFirstColumn(activity)}
            {getSecondColumn(activity)}
            {headerHasThreeColumns() && getThirdColumn(activity)}
            <Col
              className="text-end d-flex justify-content-center align-items-center"
              xs={1}
            >
              {expandedRows.includes(activity.shiftPosition.id) ? (
                <BsChevronDown
                  color="var(--black)"
                  data-testid="bs-chevron-down-icon"
                />
              ) : (
                <BsChevronRight
                  color="var(--black)"
                  data-testid="bs-chevron-right-icon"
                />
              )}
            </Col>
          </Row>
          {expandedRows.includes(activity.shiftPosition.id) ? (
            <div className="ms-2">
              <hr className="table-horizontal-line" />
              {renderActionButtons(activity)}
              <hr className="table-horizontal-line" />
              {selectedFilter !== JobShiftsQuickFilters.CURRENT_OPEN_SHIFTS && (
                <Row className="my-2">
                  <Col xs={6} className="text-start column-field-style">
                    <span>
                      <strong>WAGE</strong>
                    </span>
                  </Col>
                  {getPayRateColumn(activity, false)}
                </Row>
              )}

              <Row className="my-2">
                <Col className="text-start column-field-style">
                  <span>
                    <strong>SCHEDULED SHIFT TIME</strong>
                  </span>
                </Col>
                <Col className="text-start column-field-style">
                  <span>
                    {getFormattedShiftTimes(
                      activity.shiftPosition,
                      job?.timezone
                    )}
                  </span>
                </Col>
              </Row>
              {!selectedFilter ||
                (selectedFilter !== JobShiftsQuickFilters.ONBOARDING && (
                  <Row className="my-2">
                    <Col xs={6} className="text-start column-field-style">
                      <strong>SHIFT STATUS</strong>
                    </Col>
                    {getShiftStatus(activity, false)}
                  </Row>
                ))}
              {selectedFilter !== JobShiftsQuickFilters.NO_CHECK_IN_OUT && (
                <>
                  <Row className="my-2">
                    <Col xs={6} className="text-start column-field-style">
                      <strong>CHECK IN</strong>
                    </Col>
                    <Col xs={6} className="text-start column-field-style">
                      {getCheckInValue(
                        job,
                        activity,
                        undefined,
                        handleMissingActivityNoticed
                      )}
                    </Col>
                  </Row>
                  <Row className="my-2">
                    <Col xs={6} className="text-start column-field-style">
                      <strong>CHECK OUT</strong>
                    </Col>
                    <Col xs={6} className="text-start column-field-style">
                      {getCheckOutValue(
                        job,
                        activity,
                        undefined,
                        handleMissingActivityNoticed
                      )}
                    </Col>
                  </Row>
                </>
              )}
              {[
                JobShiftsQuickFilters.MISSING_HOURS,
                JobShiftsQuickFilters.TO_APPROVE,
              ].every((item) => item !== selectedFilter) && (
                <Row className="my-2">
                  <Col xs={6} className="text-start column-field-style">
                    <strong>SUBMITTED TIME</strong>
                  </Col>
                  {getSubmittedTimeColumn(activity, false)}
                </Row>
              )}
              <Row className="my-2">
                <Col xs={6} className="text-start column-field-style">
                  <strong>TOTAL HOURS</strong>
                </Col>
                <Col xs={6} className="text-start">
                  {activity.timeSheet?.hoursWorked || '-'}
                </Col>
              </Row>
              <Row className="my-2">
                <Col xs={6} className="text-start column-field-style">
                  <strong>WORK PAY</strong>
                </Col>
                <Col xs={6} className="text-start column-field-style">
                  {activity.timeSheet?.workerEarnedMoney ? (
                    <div>
                      {'$ '}
                      {Number(activity.timeSheet?.workerEarnedMoney).toFixed(2)}
                    </div>
                  ) : (
                    <Col className="text-start column-field-style"> - </Col>
                  )}
                </Col>
              </Row>
              {selectedFilter !== JobShiftsQuickFilters.MARK_PAID && (
                <Row className="my-2">
                  <Col className="text-start column-field-style">
                    <strong>SHIFT PAID?</strong>
                  </Col>
                  {getMarkPaidColumn(activity, false)}
                </Row>
              )}
              {selectedFilter !== JobShiftsQuickFilters.ONBOARDING && (
                <Row className="my-2">
                  <Col className="text-start column-field-style">
                    <strong>ONBOARDING</strong>
                  </Col>
                  <Col className="d-flex justify-content-start align-items-start">
                    {getOnboardingButton(activity)}
                  </Col>
                </Row>
              )}

              <Row className="my-2">
                <Col colSpan={3}>
                  <hr className="table-horizontal-line" />
                </Col>
              </Row>
            </div>
          ) : null}
        </React.Fragment>
      ))}
    </div>
  );
}

export default JobShiftsTableItemMobile;
